import { FC } from 'react';
import dayjs from 'dayjs';
import { TableBody as Body } from '@mui/material';

import { TableNoData } from '../../shared/uiComponents';
import { HeaderProperties } from '../../shared/uiComponents/Table/tableProperties';
import { CustomTableRow, RowTableCell } from '../../shared/uiComponents/Table/tableHelpers';
import {
	MondaySyncChangeProperties,
	MondaySyncLogProperties,
	MondaySyncPlannedItemProperties,
} from '../../redux/API/ClientAPIHelpers/mondaySync';

export const Headers: HeaderProperties[] = [
	{ id: '1', name: 'Date', width: '160px' },
	{ id: '2', name: 'Item', width: '200px' },
	{ id: '3', name: 'Outcome', width: '110px' },
	{ id: '4', name: 'Details', width: '430px' },
];

interface TableBodyProperties {
	data: MondaySyncLogProperties[];
}

const formatValue = (value: MondaySyncChangeProperties['oldValue']) => {
	if (value === null) return '(empty)';
	if (value === '') return '(empty string)';
	if (typeof value === 'object') return JSON.stringify(value);

	return String(value);
};

const ChangeDetails: FC<Pick<MondaySyncLogProperties, 'changes' | 'snapshot'>> = ({ changes, snapshot }) => {
	if (changes == null) {
		if (!snapshot?.before) return <div>Change details unavailable.</div>;

		return (
			<details>
				<summary>Sync incomplete — previous values preserved</summary>
				<pre>{JSON.stringify(snapshot.before, null, 2)}</pre>
			</details>
		);
	}
	if (!changes.length) return <div>No field changes.</div>;

	return (
		<details>
			<summary>
				{changes.length} field change{changes.length === 1 ? '' : 's'}
			</summary>
			{changes.map((change) => (
				<div key={change.field} style={{ marginTop: '8px', overflowWrap: 'anywhere' }}>
					<strong>{change.field}</strong>
					<div>
						{formatValue(change.oldValue)} → {formatValue(change.newValue)}
					</div>
				</div>
			))}
		</details>
	);
};

export const TableBody: FC<TableBodyProperties> = ({ data }) => {
	if (!data?.length) {
		return <TableNoData spanScope={Headers.length} />;
	}

	return (
		<Body>
			{data.map((row) => (
				<CustomTableRow key={row.id}>
					<RowTableCell>{dayjs(row.createdAt).format('MM/DD/YYYY hh:mm A')}</RowTableCell>
					<RowTableCell>{row.itemName || row.mondayItemId}</RowTableCell>
					<RowTableCell>{row.outcome}</RowTableCell>
					<RowTableCell>
						{row.errorMessage && <div style={{ marginBottom: '8px' }}>{row.errorMessage}</div>}
						<ChangeDetails changes={row.changes} snapshot={row.snapshot} />
					</RowTableCell>
				</CustomTableRow>
			))}
		</Body>
	);
};

export const PlannedHeaders: HeaderProperties[] = [
	{ id: '1', name: 'Item', width: '240px' },
	{ id: '2', name: 'Outcome', width: '130px' },
	{ id: '3', name: 'Reason', width: '360px' },
];

interface PlannedTableBodyProperties {
	data: MondaySyncPlannedItemProperties[];
}

export const PlannedTableBody: FC<PlannedTableBodyProperties> = ({ data }) => {
	if (!data?.length) {
		return <TableNoData spanScope={PlannedHeaders.length} />;
	}

	return (
		<Body>
			{data.map((row) => (
				<CustomTableRow key={row.mondayItemId}>
					<RowTableCell>{row.itemName || row.mondayItemId}</RowTableCell>
					<RowTableCell>{row.plannedOutcome}</RowTableCell>
					<RowTableCell>{row.reason || '-'}</RowTableCell>
				</CustomTableRow>
			))}
		</Body>
	);
};
